<template>
    <div class="relative" :style="{height: normalizedHeight + 'px'}">
        <canvas ref="canvas"></canvas>
    </div>
</template>

<script>
import Chart from 'chart.js';
import {UI_COLORS} from '@/utils/ui-colors';

export default {
    name: 'line-chart',
    props: {
        chartData: {
            type: Object,
            required: true,
        },
        height: {
            type: [Number, String],
            required: false,
            default: 350,
        },
    },
    data() {
        return {
            chartInstance: null,
        };
    },
    computed: {
        normalizedHeight() {
            const numeric = Number(this.height);
            return Number.isFinite(numeric) && numeric > 0 ? numeric : 350;
        },
    },
    watch: {
        chartData: {
            handler() {
                this.renderOrUpdateChart();
            },
            deep: true,
        },
    },
    mounted() {
        this.renderOrUpdateChart();
    },
    beforeUnmount() {
        this.destroyChart();
    },
    methods: {
        buildData() {
            const source = this.chartData && typeof this.chartData === 'object' ? this.chartData : {};
            const labels = Array.isArray(source.labels) ? source.labels : [];
            const sourceDatasets = Array.isArray(source.datasets) ? source.datasets : [];
            const datasets = sourceDatasets.map((dataset) => {
                const nextDataset = {...dataset};
                if (!nextDataset.borderColor) {
                    nextDataset.borderColor = 'rgba(100, 100, 100, .7)';
                }
                if (!nextDataset.backgroundColor) {
                    nextDataset.backgroundColor = 'rgba(100, 100, 100, .2)';
                }
                return nextDataset;
            });

            return {labels, datasets};
        },
        buildOptions(datasetCount) {
            return {
                scales: {
                    yAxes: [{
                        ticks: {
                            beginAtZero: true,
                            fontColor: UI_COLORS.text.subtle,
                        },
                    }],
                    xAxes: [{
                        ticks: {
                            fontColor: UI_COLORS.text.subtle,
                        },
                    }],
                },
                legend: {
                    display: datasetCount > 1,
                    position: 'bottom',
                    align: 'start',
                    labels: {
                        usePointStyle: true,
                        boxWidth: 6,
                        padding: 10,
                        fontColor: UI_COLORS.text.muted,
                    },
                },
                animation: false,
                responsive: true,
                maintainAspectRatio: false,
            };
        },
        renderOrUpdateChart() {
            const context = this.$refs.canvas ? this.$refs.canvas.getContext('2d') : null;
            if (!context) {
                return;
            }

            const data = this.buildData();
            const options = this.buildOptions(data.datasets.length);

            if (!this.chartInstance) {
                this.chartInstance = new Chart(context, {
                    type: 'line',
                    data,
                    options,
                });
                return;
            }

            this.chartInstance.data = data;
            this.chartInstance.options = options;
            this.chartInstance.update();
        },
        update() {
            if (this.chartInstance) {
                this.chartInstance.update();
            } else {
                this.renderOrUpdateChart();
            }
        },
        destroyChart() {
            if (!this.chartInstance) {
                return;
            }
            this.chartInstance.destroy();
            this.chartInstance = null;
        },
    },
};
</script>
